import React, { useState, useRef, useEffect, useMemo } from "react";
import EmployeeMultiSelect from "./EmployeeMultiSelect";
import AdhocBoundSelector from "./AdhocBoundSelector";
import { faTimesCircle, faSort, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TimeslotPicker from "./TimeslotPicker";
import CompanyAddressSelection from "./CompanyAddressSelection";
import "./tooltip.css";
import axios from "axios";
import moment from "moment";
import { showToast } from "../../toasts";

/**
 *
 * @param {function} onNoClick The function to close the dialog
 * @param {array} teamEmployees The employees of the selected team
 * @param {string} selectedDay The selected day for the adhoc booking
 * @param {array} companyAddresses The company addresses of the selected team
 * @param {boolean} addressLoading The state to determine if the company addresses are still loading
 * @param {function} setRefresh The function to refresh the page
 * @param {boolean} refresh The state to determine if the page should be refreshed
 * @param {string} role The role of the user
 * @returns {JSX.Element} The adhoc booking dialog component
 */
const AdhocView = ({
  setWeeklyChangesMade,
  cleanUpBookings,
  setCleanUpBookings,
  onNoClick,
  summaryAdhocData,
  selectedDay,
  selectedCompany,
  companyAddresses,
  addressLoading,
  setRefresh,
  refresh,
  reloadTeams,
  setReloadTeams,
  isAllowedToEditCsb,
  bookingTimeRange,
  openingTime,
  closingTime,
  role,
}) => {
  const [showDropdown, setShowDropdown] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [timeslot, setTimeslot] = useState(null);
  const [address, setAddress] = useState(null);
  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
  const [adHocPermissions, setAdHocPermissions] = useState(null);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
  const [companyEmployees, setCompanyEmployees] = useState([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");

  // Get trip type settings from selectedCompany
  const allowInboundBookings = selectedCompany?.allow_inbound_bookings ?? true;
  const allowOutboundBookings = selectedCompany?.allow_outbound_bookings ?? true;
  
  // Set default bound based on what's allowed
  const getDefaultBound = () => {
    if (allowInboundBookings) return "inbound";
    if (allowOutboundBookings) return "outbound";
    return null; // No trip types allowed
  };
  
  const [bound, setBound] = useState(getDefaultBound());
  
  // Update bound when company changes or settings change
  useEffect(() => {
    // If current bound is disabled, switch to the allowed one
    if (bound === "inbound" && !allowInboundBookings && allowOutboundBookings) {
      setBound("outbound");
    } else if (bound === "outbound" && !allowOutboundBookings && allowInboundBookings) {
      setBound("inbound");
    } else if (!bound && (allowInboundBookings || allowOutboundBookings)) {
      setBound(getDefaultBound());
    }
  }, [allowInboundBookings, allowOutboundBookings, selectedCompany?.id]);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tripToCancel, setTripToCancel] = useState(null); // Store the trip ID
  const [isCancelModalOpen, setCancelModalOpen] = useState(false);
  const [adhocTrips, setAdhocTrips] = useState([]); // Store the adhoc trips
  const [isLoadingTrips, setIsLoadingTrips] = useState(true); // Add loading state
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'ascending' });

  const [showCreateConfirmModal, setShowCreateConfirmModal] = useState(false);

  const AdhocData = summaryAdhocData?.this_week;

  const allTrips = [];

  if (AdhocData) {
    Object.keys(AdhocData).forEach((day) => {
      AdhocData[day].inbound.forEach((trip) => {
        allTrips.push({ ...trip, day, tripType: "Inbound" });
      });

      AdhocData[day].outbound.forEach((trip) => {
        allTrips.push({ ...trip, day, tripType: "Outbound" });
      });
    });

    // Sort the trips by date
    allTrips.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB;
    });
  }

  const handleCancelation = (tripId) => {
    // Set the trip ID and open the modal
    setTripToCancel(tripId);
    setCancelModalOpen(true);
  };

  const handleConfirmCancelation = () => {
    setIsLoading(true);
    axios({
      method: "delete",
      url: `/trips/${tripToCancel}`,
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        setCleanUpBookings(!cleanUpBookings);
        setRefresh(!refresh);
        setWeeklyChangesMade(false);
        showToast("success", "Trip cancelled successfully");
        setTripToCancel(null);
        setCancelModalOpen(false);
        fetchAdhocTrips(); // Fetch updated adhoc trips
      })
      .catch((error) => {
        console.error("Trip cancellation failed", error);
        showToast("error", "Error cancelling trip");
        setTripToCancel(null);
        setCancelModalOpen(false);
      })
      .finally(() => {
        setIsLoading(false); // Reset loading state
      });
  };

  const bookingViewRef = useRef(null);
  const adHocRef = useRef(null);

  const toggleTooltip = () => {
    setTooltipVisible(!tooltipVisible);
  };

  const handleRemoveEmployee = (employee) => {
    setSelectedEmployees(
      selectedEmployees.filter((item) => item.id !== employee.id)
    );
  };
  // Function to check if the time of the adhoc trip is for tomorrow (a time that has already passed is selected)
  const isTomorrow = () => {
    const now = moment();
    const optionTime = moment(timeslot, "hh:mmA");
    //if the timeslot selected is before the current time, then it is for tomorrow
    return optionTime.isBefore(now);
  };

  //Function to send a request to the backend to create the adhoc trip
  const confirmAdhoc = () => {
    if (
      selectedEmployees.length > 0 &&
      timeslot &&
      address &&
      bound &&
      isAllowed()
    ) {
      const userIds = selectedEmployees.map((employee) => employee.id);
      const data = {
        user_ids: userIds,
        selected_date: selectedDate,
        timeslot: timeslot,
        address: address.name,
        bound: bound.toLowerCase(), // Ensure bound is lowercase to match backend expectations
      };
      setIsLoading(true);
      // Make the POST request to create the adhoc trips
      axios
        .post("/team_manager/create_adhoc_trip", data)
        .then((response) => {
          setCleanUpBookings(!cleanUpBookings);
          setRefresh(!refresh);
          setWeeklyChangesMade(false);

          setAddress({
            name: "Select Address",
          });
          setTimeslot(null);
          setSelectedEmployees([]);
          setBound(null);
          showToast("success", "Adhoc trip created successfully");
          fetchAdhocTrips();
          setIsLoading(false);
        })
        .catch((error) => {
          let errorMessages;
          if (error?.response?.data?.errors) {
            let errors = error.response.data.errors.reduce((acc, trip) => {
              acc[trip.error] = acc[trip.error] || [];
              acc[trip.error].push(trip.user_full_name);
              return acc;
            }, {});

            errorMessages = Object.entries(errors).map(([error, users]) => {
              return users.length > 1 ? `${users.join(", ")}: ${error}` : `${users[0]}: ${error}`;
            }).join(". ");
          }

          showToast("error", errorMessages || error?.response?.data?.error || "Error occurred while creating bookings");
          // Handle the error if needed
          setRefresh(!refresh);
          setIsLoading(false);
        });
    }
  };

  const showToolTip = () => {
    if (selectedEmployees.length == 0) {
      return "Please select the employees you want to book for.";
    } else if (!timeslot) {
      return "Please select a timeslot to book for.";
    } else if (!address) {
      return "Please select a company address to book for.";
    } else if (!bound) {
      return "Please select a bound.";
    }
  };

  //Check if all required data is present
  const isAllowed = () => {
    if (timeslot == null) {
      return false;
    }
    if (selectedEmployees.length == 0) {
      return false;
    }
    if (address == null || address == "") {
      return false;
    }
    if (bound == null) {
      return false;
    }
    return true;
  };

  const filteredTrips = adhocTrips.filter((trip) => {
    const searchString =
      trip.full_name +
      trip.employee_number +
      trip.day +
      trip.location +
      trip.destination +
      trip.timeslot +
      trip.status +
      trip.tripType;
    return searchString.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const sortedAndFilteredTrips = useMemo(() => {
    let sortableItems = [...filteredTrips];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        // Ensure values exist before comparison, treat null/undefined as lowest
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === null || aValue === undefined) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (bValue === null || bValue === undefined) return sortConfig.direction === 'ascending' ? 1 : -1;


        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredTrips, sortConfig]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'ascending'
    ) {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (!sortConfig || sortConfig.key !== key) {
      return faSort;
    }
    return sortConfig.direction === 'ascending' ? faSortUp : faSortDown;
  };

  // Modal component
  const ConfirmationModal = ({ isOpen, onCancel, onConfirm, isLoading }) => {
    if (!isOpen) {
      return null;
    }

    return (
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div className="modal-bg absolute inset-0 bg-gray-800 bg-opacity-50"></div>
        <div className="modal relative bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 mb-4">
              <svg className="w-full h-full text-yellow-500" fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-800 mb-2">Cancel Trip</h3>
            <p className="text-center text-gray-600 mb-6 w-full break-words">Are you sure you want to cancel this trip? <br/> This action cannot be undone.</p>
            <div className="flex justify-center w-full space-x-4">
              <button
                onClick={onCancel}
                disabled={isLoading}
                className={`px-4 py-2 rounded text-gray-800 bg-gray-200 hover:bg-gray-300 transition-colors font-medium ${
                  isLoading ? "cursor-not-allowed opacity-50" : ""
                }`}
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                className={`px-4 py-2 rounded text-white bg-purple-600 hover:bg-purple-700 transition-colors font-medium flex items-center justify-center ${
                  isLoading ? "cursor-not-allowed opacity-50" : ""
                }`}
              >
                {isLoading ? (
                  <div className="loader ease-linear rounded-full border-2 border-t-2 border-gray-200 h-5 w-5"></div>
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Confirmation modal for creating trips
  const CreateTripsConfirmationModal = ({
    isOpen, onCancel, onConfirm, count, timeslot, date, tripType, isLoading
  }) => {
    if (!isOpen) return null;
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div className="modal-bg absolute inset-0 bg-gray-800 bg-opacity-50"></div>
        <div className="modal relative bg-white p-8 rounded-xl shadow-2xl max-w-md w-full mx-4 flex flex-col items-center">
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-purple-100 mb-4">
            <svg className="w-8 h-8 text-purple-500" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h8M12 8v8" />
            </svg>
          </div>
          <h3 className="text-2xl font-semibold text-gray-800 mb-2 text-center">
            Create {count} Trip{count > 1 ? 's' : ''}?
          </h3>
          <div className="w-full border-b border-gray-200 my-3"></div>
          <div className="w-full grid grid-cols-2 gap-y-2 gap-x-4 text-gray-700 text-base mb-6">
            <div className="font-medium">Date:</div>
            <div>{date}</div>
            <div className="font-medium">Timeslot:</div>
            <div>{timeslot}</div>
            <div className="font-medium">Trip Type:</div>
            <div className="capitalize">{tripType}</div>
            <div className="font-medium">Employees:</div>
            <div>{count}</div>
          </div>
          <div className="flex justify-center w-full space-x-4 mt-2">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="px-5 py-2 rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 transition font-medium border border-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="px-6 py-2 rounded-lg text-white bg-purple-600 hover:bg-purple-700 transition font-semibold flex items-center justify-center shadow-md"
            >
              {isLoading ? (
                <div className="loader ease-linear rounded-full border-2 border-t-2 border-gray-200 h-5 w-5"></div>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Create
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    // Function to handle the click outside the adhoc component
    const handleClickOutside = (event) => {
      if (
        adHocRef.current &&
        bookingViewRef.current &&
        !adHocRef.current.contains(event.target) &&
        !bookingViewRef.current.contains(event.target)
      ) {
        onNoClick();
      }
    };

    // Add the event listener when the component mounts
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Add useEffect to fetch company employees
  useEffect(() => {
    if (!selectedCompany?.id) {
      setCompanyEmployees([]);
      setAdhocTrips([]);
      setAdHocPermissions({
        manager_ad_hoc_permission: false,
        employee_ad_hoc_permission: false,
        company_admin_ad_hoc_permission: false,
        logistic_specialist_ad_hoc_permission: false,
      });
      setIsLoadingEmployees(false);
      setIsLoadingTrips(false);
      setIsLoadingPermissions(false);
      return;
    }

    if (selectedCompany?.id) {
      setIsLoadingEmployees(true);
      axios({
        method: "get",
        url: `/team_manager/find_company_employees`,
        headers: { Accept: "application/json" },
        params: {
          company_id: selectedCompany.id,
        },
      })
        .then((response) => {
          setCompanyEmployees(response.data);
          setIsLoadingEmployees(false);
        })
        .catch((error) => {
          console.error("Company employees fetch failed", error);
          setIsLoadingEmployees(false);
        });
    }
  }, [selectedCompany?.id]);

  // Update the fetchAdhocTrips function to use company_id instead of team_id
  const fetchAdhocTrips = () => {
    setIsLoadingTrips(true);
    axios({
      method: "get",
      url: `/team_manager/get_adhoc_trips`,
      headers: { Accept: "application/json" },
      params: {
        company_id: selectedCompany?.id,
        date: selectedDay,
      },
    })
      .then((response) => {
        setAdhocTrips(response?.data);
        setIsLoadingTrips(false);
      })
      .catch((error) => {
        console.error("Adhoc trips fetch failed", error);
        setIsLoadingTrips(false);
      });
  };

  useEffect(() => {
    if (selectedCompany?.id) {
      fetchAdhocTrips();
    }
  }, [selectedCompany?.id, selectedDay]);

  // Add useEffect to fetch permissions
  useEffect(() => {
    if (!selectedCompany?.id) return; // Don't make the request if no company ID

    setIsLoadingPermissions(true);
    axios({
      method: 'get',
      url: `/companies/${selectedCompany.id}/ad_hoc_permission.json`,
      headers: { Accept: 'application/json' }
    })
      .then(response => {
        setAdHocPermissions(response.data);
        setIsLoadingPermissions(false);
      })
      .catch(error => {
        console.error('Error fetching ad-hoc permissions:', error?.response?.data || error.message);
        // Set default permissions (all false) in case of error
        setAdHocPermissions({
          manager_ad_hoc_permission: false,
          employee_ad_hoc_permission: false,
          company_admin_ad_hoc_permission: false,
          logistic_specialist_ad_hoc_permission: false,
          client_manager_ad_hoc_permission: false
        });
        setIsLoadingPermissions(false);
      });
  }, [selectedCompany?.id]); // Only re-run if company ID changes

  /** Uses logged-in `role` prop — client manager is separate from manager (no substring match). */
  const hasAdHocPermission = () => {
    if (!adHocPermissions) return false;
    switch (role) {
      case 'admin':
        return true;
      case 'client_manager':
        return !!adHocPermissions.client_manager_ad_hoc_permission;
      case 'company_admin':
        return !!adHocPermissions.company_admin_ad_hoc_permission;
      case 'logistics_specialist':
        return !!adHocPermissions.logistic_specialist_ad_hoc_permission;
      case 'employee':
        return !!adHocPermissions.employee_ad_hoc_permission;
      case 'manager':
        return !!adHocPermissions.manager_ad_hoc_permission;
      default:
        return false;
    }
  };

  return (
    <div ref={bookingViewRef}>
      <div ref={adHocRef}>
        <div className="p-4 bg-gray-200 bg-opacity-50 rounded my-4" style={{ whiteSpace: 'pre-wrap' }}>
          <h1 className="text-gray-600 text-lg py-2">
            Adhoc Bookings for{" "}
            <span className=" font-medium text-purple-500">
              {selectedCompany?.name}
            </span>{" "}
          </h1>
          <h3 className="text-gray-600 mb-1 text-sm">Date - {selectedDay} </h3>
          <p className="text-gray-500 text-sm">
            Adhoc bookings outside the normal schedule can be made here.
            Additional charges may apply.{" "}
            <br/>
            <span className="text-gray-700 italic text-sm">
              Any ad-hoc bookings for timeslots before the current time, will be
              made for tomorrow at that time.
            </span>
          </p>
        </div>

        {isLoadingPermissions ? (
          <div className="p-4 bg-white shadow-md rounded my-6 flex justify-center">
            <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-8 w-8"></div>
          </div>
        ) : hasAdHocPermission() ? (
          <>
            <div className="w-full my-1">
              <EmployeeMultiSelect
                showDropdown={showDropdown}
                setShowDropdown={setShowDropdown}
                availableEmployees={companyEmployees}
                selectedEmployees={selectedEmployees}
                setSelectedEmployees={setSelectedEmployees}
                excludeSelected={true}
                isAdhoc={true}
              />
            </div>

            {selectedEmployees.length > 0 && (
              <div className="w-full mt-4">
                <div className="flex flex-wrap justify-center items-center overflow-y-auto">
                  {selectedEmployees.map((employee) => (
                    <div
                      className="flex items-center justify-between bg-gray-200 text-gray-700 p-2 rounded-full m-1 "
                      style={{ height: "26px" }}
                    >
                      <p key={employee.id} className="text-sm">
                        {employee.name} {employee.surname}
                      </p>
                      <button
                        onClick={() => handleRemoveEmployee(employee)}
                        className="px-2 cursor-pointer "
                      >
                        <FontAwesomeIcon
                          icon={faTimesCircle}
                          className="ml-2 text-gray-500"
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex w-full my-2 justify-between space-x-1">
              <div className="w-1/4">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={moment().format('YYYY-MM-DD')}
                  className="w-full px-3 py-2 text-sm border rounded-md h-[42px] bg-white"
                />
              </div>
              <div className="w-1/4">
                <TimeslotPicker
                  timeSlot={timeslot}
                  setTimeSlot={setTimeslot}
                  selectedDay={selectedDate}
                  selectedEmployees={selectedEmployees}
                  isAdhoc={true}
                  bookingTimeRange={bookingTimeRange}
                  openingTime={openingTime}
                  closingTime={closingTime}
                />
              </div>
              <div className="w-1/4">
                <CompanyAddressSelection
                  selectedCompanyAddress={address}
                  setSelectedCompanyAddress={setAddress}
                  companyAddresses={companyAddresses}
                  addressLoading={addressLoading}
                  selectedEmployees={selectedEmployees}
                />
              </div>
              <div className="w-1/4">
                <AdhocBoundSelector
                  tripType={bound}
                  allowInboundBookings={selectedCompany?.allow_inbound_bookings ?? true}
                  allowOutboundBookings={selectedCompany?.allow_outbound_bookings ?? true}
                  handleTabSelect={setBound}
                />
              </div>
            </div>

            <div className="flex justify-end mt-4 border-gray-200">
              <button
                onClick={isAllowed() ? () => setShowCreateConfirmModal(true) : () => {}}
                className={`px-6 py-1 text-white rounded-xl flex align-center justify-center relative focus:outline-none ${
                  !isAllowed()
                    ? "cursor-not-allowed bg-gray-300"
                    : "cursor-pointer bg-purple-500 hover:bg-purple-600"
                } `}
                onMouseEnter={toggleTooltip}
                onMouseLeave={toggleTooltip}
                style={{ zIndex: 11 }}
              >
                Confirm
                {tooltipVisible && !isAllowed() && (
                  <div className="tooltip w-full" style={{ zIndex: 11 }}>
                    {showToolTip()}
                  </div>
                )}
              </button>
              {isLoading && (
                <>
                  <div className="absolute top-0 left-0 w-full h-full bg-gray-500 bg-opacity-50 z-50 flex items-center justify-center rounded-lg">
                    <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-8 w-8 my-1"></div>
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <div className="p-4 bg-white shadow-md rounded my-6">
            <p className="text-gray-600 text-sm text-center">
              You do not have permission to make ad-hoc bookings.
            </p>
          </div>
        )}
      </div>

      {adhocTrips && adhocTrips.length > 0 && (
        <div className="pb-2 pt-4 w-1/3">
          <input
            type="text"
            placeholder="Search..."
            className="w-full px-3 py-1 text-sm border rounded-md"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
      )}

      {sortedAndFilteredTrips.length > 0 ? (
        <>
          <div className="bg-white shadow-md rounded mt-2 mb-6 border border-gray-200">
            {isLoadingTrips ? (
              <div className="p-4 flex justify-center">
                <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-8 w-8"></div>
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                <table className="min-w-full table-auto text-sm">
                  <thead>
                    <tr className="bg-gray-100 text-gray-600 uppercase text-xs leading-normal">
                      <th className="py-3 px-4 text-left cursor-pointer" onClick={() => requestSort('full_name')}>
                        Name <FontAwesomeIcon icon={getSortIcon('full_name')} className="ml-1" />
                      </th>
                      <th className="py-3 px-4 text-left cursor-pointer" onClick={() => requestSort('employee_number')}>Employee Number</th>
                      <th className="py-3 px-4 text-left cursor-pointer" onClick={() => requestSort('date')}>
                        Day <FontAwesomeIcon icon={getSortIcon('date')} className="ml-1" />
                      </th>
                      <th className="py-3 px-4 text-left cursor-pointer" onClick={() => requestSort('location')}>
                        Location <FontAwesomeIcon icon={getSortIcon('location')} className="ml-1" />
                      </th>
                      <th className="py-3 px-4 text-left cursor-pointer" onClick={() => requestSort('timeslot')}>
                        Time Slot <FontAwesomeIcon icon={getSortIcon('timeslot')} className="ml-1" />
                      </th>
                      <th className="py-3 px-4 text-left cursor-pointer" onClick={() => requestSort('status')}>
                        Status <FontAwesomeIcon icon={getSortIcon('status')} className="ml-1" />
                      </th>
                      <th className="py-3 px-4 text-left cursor-pointer" onClick={() => requestSort('trip_type')}>
                        Trip Type <FontAwesomeIcon icon={getSortIcon('trip_type')} className="ml-1" />
                      </th>
                      <th className="py-3 px-4 text-left"></th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600 text-xs">
                    {sortedAndFilteredTrips?.map((trip) => (
                      <tr key={`${trip.id}-${trip.date}-${trip.timeslot}-${trip.trip_type}`} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="py-3 px-4 text-left whitespace-nowrap">{trip.full_name}</td>
                        <td className="py-3 px-4 text-left">{trip.employee_number}</td>
                        <td className="py-3 px-4 text-left">
                          {moment(trip.date).format("YYYY-MM-DD")}
                        </td>
                        <td className="py-3 px-4 text-left">{trip.location}</td>
                        <td className="py-3 px-4 text-left">{trip.timeslot}</td>
                        <td className="py-3 px-4 text-left">{trip.status}</td>
                        <td className="py-3 px-4 text-left">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              trip.trip_type === 'inbound'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {trip.trip_type}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-left">
                          {trip.status == "pending" && (
                            <button
                              className={`p-2 rounded text-white flex items-center justify-center ${
                                hasAdHocPermission()
                                  ? "bg-red-500 hover:bg-red-600 cursor-pointer"
                                  : "bg-gray-400 cursor-not-allowed opacity-50"
                              }`}
                              onClick={() => hasAdHocPermission() && handleCancelation(trip.id)}
                              title={hasAdHocPermission() ? "Cancel Trip" : "You don't have permission to cancel trips"}
                              disabled={!hasAdHocPermission()}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                                />
                              </svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="p-4 bg-white shadow-md rounded my-6">
          <p className="text-gray-600 text-sm text-center">No adhoc trips.</p>
        </div>
      )}

      <CreateTripsConfirmationModal
        isOpen={showCreateConfirmModal}
        isLoading={isLoading}
        onCancel={() => setShowCreateConfirmModal(false)}
        onConfirm={() => {
          setShowCreateConfirmModal(false);
          confirmAdhoc();
        }}
        count={selectedEmployees.length}
        timeslot={timeslot}
        date={selectedDate}
        tripType={bound}
      />

      <ConfirmationModal
        isOpen={isCancelModalOpen}
        isLoading={isLoading}
        onCancel={() => {
          setTripToCancel(null);
          setCancelModalOpen(false);
        }}
        onConfirm={handleConfirmCancelation}
      />
    </div>
  );
};

export default AdhocView;
